/**
 * QMP-FE-3 — STEP 3 "Handover": 3a Handover rules · 3b Punch list at handover.
 *
 * LAYOUT, COPY AND VALUES ARE THE MOCKUP'S (`mockup-qmp-v79-official.html`,
 * `#w3`). Two renderers exist in that file for this step; the live one is the
 * pair reached by `renderSub` (v79:1252-1253):
 *   · 3a — `render3aFlow()` (v51), a FLOW BUILDER: the path IS the form, and
 *     each arrow carries the two settings of that handover;
 *   · 3b — the generic `rt()` table, in the `.rt2` look of step 2.
 * Everything under `#rt3a` (`render3a`, `render3aQ`, `S.hoRules`, `S.plRules`)
 * is dead code — no such element exists in the page.
 *
 * The grammar lives in `lib/qmpRules`; the state in `useQmpRuleEditor`, held
 * by the page; the subarea card, the table, the subnav, the side cards and the
 * "?" popover are step 2's (`ItpRulesStep`).
 *
 * NO BUSINESS RULE IS REPEATED HERE. Which paths a package may walk is the
 * grammar (the server's own two structural conditions, in `lib/qmpRules`);
 * everything else the server decides, and its refusal is drawn on the row of
 * the package it names.
 *
 * QMP-UNIT-1 (137 B) — no unit control: each hop keeps the unit it has; the standard is the server's.
 */
import React from 'react';
import {
  Subnav, SubCard, SideCards, LoadError, useTip, sectionCtx, QButton, ValueSelect, RuleFoot,
  HowThisWorks, Resp, reviewText,
  T_ERR, T_RWHY, T_RWARN, T_SMALL_BTN, T_X_BTN, T_SUB2, T_TH, T_BAD_BAR, T_SUM_TD,
  T_RP, T_MISS, T_DIMTXT,
} from './ItpRulesStep';
import {
  lines, setGeneral, setPackageRule, removeOwnRule, setHandoverPath, standardHandovers,
  receiverLimitOf, setReceiverLimit, formatReceiverLimit, RECEIVER_LIMIT_LABEL,
  PATH_POSITIONS, POSITION_LABEL, pathFitsRow, fieldOptions,
  formatHandoverUnit, formatHandoverNotice,
  formatShortNotice, formatHandoverCount,
  punchHasTwoHandovers, setPerHandover, setPunchStep,
} from '../../lib/qmpRules';

// QMP-FE-7 — the titles are the server's (`editor.titles`).
export const SUBS3 = ['handover', 'punch_list'];

// Mockup `details.more div ul` — the "How this works" lists of `#s3a`.
const UL = 'list-disc pl-[18px] mb-[8px] [&>li]:mb-[3px]';

// ---------------------------------------------------------------------------
// tooltips (mockup `.tip` bodies of `#s3a`)
// ---------------------------------------------------------------------------
const TIPS3 = {
  path: {
    title: 'Handover path',
    body: (
      <>
        Who hands the assets and their QA records to whom. <b>Executing → Verification →
        Acceptance:</b> two handovers. <b>Executing → Acceptance:</b> one handover; the
        Verification Party checks the work but does not receive it. <b>Executing →
        Verification:</b> one handover, for packages with no Acceptance Party. <b>No
        handover:</b> internal QA. The companies come from Step 1.
      </>
    ),
  },
  pl: {
    title: 'Punch list & QA Complete',
    body: (
      <>
        <b>No:</b> every punch list item must be closed before the asset reaches QA
        Complete. <b>Yes:</b> items in the selected categories may still be open; QA Complete does
        not close or hide them — they remain open and visible until they are closed. Punch list
        items do not exist before the handover inspection.
      </>
    ),
  },
  plc: {
    title: 'Categories that may stay open',
    body: (
      <>
        Category A — must be fixed and accepted before the handover of the asset can
        complete. Always blocks handover and QA Complete. Category B — must be fixed. Does not
        hold the handover. May stay open at QA Complete only if this rule allows it. Category
        C — minor or cosmetic. Does not hold the handover. May stay open at QA Complete only if
        this rule allows it.
      </>
    ),
  },
  // D16 A2 — the field help the mockup never wired (`tipStage2`), and the
  // owner's ruling that a short-notice booking is REFUSED, not flagged.
  notice: {
    title: 'Minimum notice to the receiving party',
    body: (
      <>
        How far in advance the handover inspection must be booked. A booking with less notice is
        refused. There is no short-notice exception.
      </>
    ),
  },
  // QMP-FE-7 — §19 (QMP-BE-2, D22).
  limit: {
    title: RECEIVER_LIMIT_LABEL,
    body: (
      <>
        Time the receiving company has to sign the handover inspection, counted in working days
        from the booked inspection time. Once it passes, the organiser may complete the handover
        without that signature, giving a reason that stays on the record.
      </>
    ),
  },
};

// ---------------------------------------------------------------------------
// mockup tokens of the flow builder (v79:265-284)
// ---------------------------------------------------------------------------
const FB = 'border border-[#16223a] rounded-[12px] bg-[#0d1526] px-[16px] py-[14px] mb-[12px]';
const FB_GEN = `${FB} border-l-[3px] border-l-[#f97316]`;
const KICK = 'text-[10.5px] tracking-[.1em] uppercase text-[#5b6b85] font-bold mb-[6px]';
const FBH = 'flex items-center gap-[12px] flex-wrap mb-[10px]';
const FBH_T = 'font-bold text-[14px]';
const FBH_S = 'text-[#94a3b8] text-[12px]';
const PATH_CONTROL = 'w-auto min-w-[280px] max-w-full bg-[#0b1322] border border-[#1c2941] '
  + 'text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px] disabled:opacity-60';
const FLOW = 'flex items-stretch gap-[10px] flex-wrap';
const NODE = 'flex-[0_0_170px] bg-[#0b1222] border border-[#16223a] rounded-[10px] '
  + 'px-[12px] py-[10px] flex flex-col justify-center';
const NROLE = 'text-[10.5px] tracking-[.08em] uppercase text-[#94a3b8]';
const NCO = 'font-bold mt-[4px] text-[13px]';
const NCO_DIM = 'mt-[4px] text-[13px] text-[#5b6b85] font-medium italic';
const ARROW = 'flex-1 min-w-[220px] relative pt-[12px] pr-[26px] pb-[6px] pl-[10px] grid gap-[6px] self-center '
  + "before:content-[''] before:absolute before:left-0 before:right-[14px] before:top-0 before:border-t-2 before:border-[#f97316] "
  + "after:content-[''] after:absolute after:right-[2px] after:top-[-6px] after:w-0 after:h-0 "
  + 'after:border-l-[12px] after:border-l-[#f97316] after:border-y-[7px] after:border-y-transparent';
const AL = 'text-[10.5px] text-[#f97316] tracking-[.08em] uppercase font-bold';
const MF = 'grid gap-[3px]';
const MF_LABEL = 'text-[10.5px] text-[#94a3b8] tracking-[.04em]';
const MF_CONTROL = 'w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] '
  + 'px-[10px] py-[8px] text-[12.5px] disabled:opacity-60';
const NOHO = 'flex-1 flex items-center text-[#94a3b8] text-[12.5px] px-[8px]';
const EXH = 'flex items-baseline gap-[10px] mt-[18px] mb-[10px]';
const EXH_T = 'text-[10.5px] tracking-[.1em] uppercase text-[#5b6b85]';
const EXH_S = 'text-[#5b6b85] text-[12px]';
const EXROW = 'flex items-center gap-[14px] flex-wrap';
const NM = 'min-w-[180px] [&>b]:block';
const EXSUM = 'flex-1 flex items-center gap-[7px] flex-wrap text-[12.5px] text-[#cbd5e1]';
const EXSUM_AR = 'text-[#f97316]';
const EXSUM_ST = 'text-[#94a3b8] text-[12px]';
const ACT = 'flex gap-[8px]';
const EXBODY = 'mt-[12px] pt-[12px] border-t border-[#16223a]';
const BTN_GHOST_SM = `${T_SMALL_BTN} border border-[#1c2941] text-[#94a3b8] disabled:opacity-45`;

// ---------------------------------------------------------------------------
// 3a — the flow builder (mockup `render3aFlow`, `chainForm`, `chainSummary`)
// ---------------------------------------------------------------------------

/** Mockup `nm` inside `chainForm`: the first row does not know the companies,
 *  a package row names them (or says the Executing Party is not appointed). */
function companyAt(row, position) {
  if (position === 'execution') {
    return row.executing && row.executing.appointed !== false
      ? row.executing.name : 'To be appointed';
  }
  return row[position] ? row[position].name : null;
}

function Node({ row, position }) {
  return (
    <div className={NODE}>
      <div className={NROLE}>{POSITION_LABEL[position]}</div>
      {row
        ? <div className={NCO}>{companyAt(row, position) || '—'}</div>
        : <div className={NCO_DIM}>varies by package</div>}
    </div>
  );
}

/** Mockup `pathCellQ`: the first row offers every served path, a package row
 *  only the ones it can walk. */
function PathSelect({
  value, row, onChange, disabled, k, editor,
}) {
  const path = (value && value.path) || 'none';
  return (
    <ValueSelect
      served={fieldOptions(editor.options, 'handover', 'path').filter((o) => pathFitsRow(o.value, row))}
      current={path}
      fallback={(p) => p}
      disabled={disabled}
      className={PATH_CONTROL}
      onPick={(p) => onChange(setHandoverPath(value, p, standardHandovers(editor.ctx.standardRules)))}
      testId={`qh-path-${k}`}
    />
  );
}

/** Mockup `arrow`: one per handover, with its two settings. */
function Hop({
  value, index, onChange, disabled, k, openTip, options,
}) {
  const step = ((value && value.handovers) || [])[index] || {};
  const set = (patch) => {
    const list = ((value && value.handovers) || []).map((h) => ({ ...h }));
    list[index] = { ...(list[index] || {}), ...patch };
    onChange({ ...value, handovers: list });
  };
  return (
    <div className={ARROW} data-testid={`qh-hop-${k}-${index}`}>
      <div className={AL}>{`${index === 0 ? '1st' : '2nd'} handover`}</div>
      <div className={MF}>
        <label className={MF_LABEL}>
          Minimum notice to the receiving party
          <QButton tipKey="notice" onOpen={openTip} />
        </label>
        <ValueSelect
          served={fieldOptions(options, 'handover', 'handovers[].notice')}
          current={step.notice}
          fallback={formatHandoverNotice}
          disabled={disabled}
          className={MF_CONTROL}
          onPick={(n) => set({ notice: n })}
          testId={`qh-notice-${k}-${index}`}
        />
      </div>
    </div>
  );
}

/** Mockup `chainForm`: the path drawn as nodes and arrows; the arrows ARE the
 *  form. */
function ChainForm({
  value, row, onChange, disabled, k, openTip, options,
}) {
  const path = (value && value.path) || 'none';
  const seq = PATH_POSITIONS[path] || ['execution'];
  if (path === 'none') {
    return (
      <div className={FLOW} data-testid={`qh-flow-${k}`}>
        <Node row={row} position="execution" />
        <div className={NOHO} data-testid={`qh-noho-${k}`}>
          No handover. The company closes out each asset with a QA Closeout Inspection: its own
          people attend and sign, punch list items are raised, fixed and accepted internally, and
          the asset reaches QA Complete once the punch list rule is met.
        </div>
      </div>
    );
  }
  return (
    <div className={FLOW} data-testid={`qh-flow-${k}`}>
      {seq.map((p, i) => (
        <React.Fragment key={p}>
          <Node row={row} position={p} />
          {i < seq.length - 1 && (
            <Hop
              value={value}
              index={i}
              onChange={onChange}
              disabled={disabled}
              k={k}
              openTip={openTip}
              options={options}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3a — "If the receiving company does not sign" (§19, QMP-FE-7): one line
// under the flow of each rule, the first block and every exception
// ---------------------------------------------------------------------------
const LIMIT_ROW = 'flex items-center gap-[10px] flex-wrap mt-[12px]';
const LIMIT_SELECT = 'w-auto min-w-[160px] bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] '
  + 'rounded-[8px] px-[10px] py-[8px] text-[12.5px] disabled:opacity-60';
const LIMIT_DAYS = 'w-[72px] bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] '
  + 'px-[10px] py-[8px] text-[12.5px] disabled:opacity-60';
const LIMIT_HELP = 'text-[#5b6b85] text-[12px] mt-[6px]';
export const LIMIT_LABEL = RECEIVER_LIMIT_LABEL;
export const LIMIT_HELP_NONE = 'The handover stays open until the receiving company signs.';
export const LIMIT_HELP_DAYS = 'After the limit, the organiser can complete the handover with a reason. '
  + 'The record then says the receiving company did not sign, and that company is told.';

/** The served "No time limit" / "Time limit"; with "Time limit", the number of
 *  working days (the served `input`: at least 1). The server's refusal of the
 *  field (422 `INVALID_RULE`) is drawn under it, in its own words. */
function ReceiverLimit({
  value, onChange, disabled, k, openTip, options, error,
}) {
  const limit = receiverLimitOf(value);
  const served = fieldOptions(options, 'handover', 'receiver_signature_limit');
  const timed = served.find((o) => o.value && o.value.kind === 'days');
  const min = (timed && timed.input && timed.input.min) || 1;
  const days = limit.kind === 'days';
  const set = (l) => onChange(setReceiverLimit(value, l));
  return (
    <div data-testid={`qh-limit-${k}`}>
      <div className={LIMIT_ROW}>
        <span className={FBH_S}>
          {LIMIT_LABEL}
          <QButton tipKey="limit" onOpen={openTip} />
        </span>
        <ValueSelect
          served={served}
          current={limit}
          same={(a, b) => Boolean(a && b && a.kind === b.kind)}
          fallback={formatReceiverLimit}
          disabled={disabled}
          className={LIMIT_SELECT}
          onPick={(v) => set(v.kind === 'days'
            ? { kind: 'days', value: days ? limit.value : v.value }
            : { kind: 'none' })}
          testId={`qh-limit-kind-${k}`}
        />
        {days && (
          <>
            <input
              type="number"
              min={min}
              step={1}
              className={LIMIT_DAYS}
              value={limit.value == null ? '' : String(limit.value)}
              disabled={disabled}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === '' ? null : Number(raw);
                set({ kind: 'days', value: Number.isFinite(n) ? n : null });
              }}
              data-testid={`qh-limit-days-${k}`}
            />
            <span className={FBH_S}>working days after the inspection</span>
          </>
        )}
      </div>
      <div className={LIMIT_HELP} data-testid={`qh-limit-help-${k}`}>
        {days ? LIMIT_HELP_DAYS : LIMIT_HELP_NONE}
      </div>
      {error && <div className={`mt-[6px] ${T_ERR}`} data-testid={`qh-limit-error-${k}`}>{error}</div>}
    </div>
  );
}

/** A refusal of the limit field (`field` = `handover.receiver_signature_limit…`). */
const isLimitError = (err) => Boolean(err && typeof err.field === 'string'
  && err.field.startsWith('handover.receiver_signature_limit'));

/** What the limit field says of its refusal (QMP-FE-9): the server's `reason`
 *  when it is a sentence (QMP-BE-3: ends in "." or has a space), else — a
 *  code such as `extra_forbidden` — the refusal's `message`. */
const limitErrorText = (err) => {
  const reason = typeof err.reason === 'string' ? err.reason.trim() : '';
  return reason && (reason.endsWith('.') || reason.includes(' ')) ? reason : err.message;
};

/** Mockup `chainSummary`: the closed line on an exception row. */
function ChainSummary({ value, row }) {
  const path = (value && value.path) || 'none';
  const seq = PATH_POSITIONS[path] || [];
  if (path === 'none') return <span className={EXSUM_ST}>No handover</span>;
  const steps = (value && value.handovers) || [];
  return (
    <>
      {seq.map((p, i) => (
        <React.Fragment key={p}>
          <span>{companyAt(row, p) || '—'}</span>
          {i < seq.length - 1 && (
            <>
              <span className={EXSUM_AR}>→</span>
              <span className={EXSUM_ST}>
                {`${formatHandoverUnit(steps[i] && steps[i].unit)} · ${formatShortNotice(steps[i] && steps[i].notice)}`}
              </span>
              <span className={EXSUM_AR}>→</span>
            </>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

/** The whole of 3a: the first block, the packages with a rule of their own,
 *  the way to add one, and the refusals. */
function HandoverFlow({ section, editor, openTip }) {
  const [editing, setEditing] = React.useState(() => new Set());
  const toggle = (id) => setEditing((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const state = editor.states[section];
  if (!state) return null;
  const ctx = sectionCtx(editor, section);
  const { structure } = ctx;
  const l = lines(state, structure);
  const err = editor.errors[section];
  const review = editor.status[section].review;
  const disabled = !editor.canGovern || Boolean(editor.saving);
  const upd = (fn) => editor.update(section, fn);
  const onGeneral = (v) => upd((st) => setGeneral(section, st, v, ctx));
  const ownIds = new Set(l.rows.map((r) => r.row.qwp_id));
  const flagged = Object.keys(review).length;

  // A refusal or a review mark of a package with no row of its own belongs to
  // the first block (as it belongs to the first row in steps 2 and 4).
  const limitErr = isLimitError(err);
  const errName = (id) => {
    const r = structure.find((x) => x.qwp_id === id);
    return r ? r.name : id;
  };
  const firstNotes = [];
  if (err && err.qwpId && !ownIds.has(err.qwpId) && !limitErr) {
    const r = structure.find((x) => x.qwp_id === err.qwpId);
    firstNotes.push({
      key: 'err',
      cls: T_ERR,
      text: `${r ? r.name : err.qwpId}: ${err.message}`,
      testId: `qr-line-error-${section}-${err.qwpId}`,
    });
  }
  l.free.filter((r) => review[r.qwp_id]).forEach((r) => {
    firstNotes.push({
      key: r.qwp_id,
      cls: 'text-[11px] text-[#fbbf24]',
      text: `${r.name}: ${reviewText(review[r.qwp_id])}`,
      testId: `qr-review-${section}-${r.qwp_id}`,
    });
  });

  return (
    <div data-testid="qh-flow">
      {flagged > 0 && (
        <div className={`mb-[10px] ${T_ERR}`} data-testid="qh-error">
          {`Every package needs a handover path it can follow — ${flagged} flagged below.`}
        </div>
      )}

      <div className={FB_GEN} data-testid="qh-first">
        <div className={KICK}>Quality Work Package</div>
        <div className={FBH}>
          <span className={FBH_T} data-testid="qh-first-label">{l.first.label}</span>
          <span className={FBH_S} data-testid="qh-first-sub">{l.first.sub}</span>
        </div>
        <div className={FBH}>
          <span className={FBH_S}>
            Handover path
            <QButton tipKey="path" onOpen={openTip} />
          </span>
          <PathSelect
            value={state.general}
            row={null}
            onChange={onGeneral}
            disabled={disabled}
            k="all"
            editor={editor}
          />
          <span className={FBH_S} data-testid="qh-count-all">
            {formatHandoverCount((state.general && state.general.path) || 'none')}
          </span>
        </div>
        <ChainForm
          value={state.general}
          row={null}
          onChange={onGeneral}
          disabled={disabled}
          k="all"
          openTip={openTip}
          options={editor.options}
        />
        {((state.general && state.general.path) || 'none') !== 'none' && (
          <ReceiverLimit
            value={state.general}
            onChange={onGeneral}
            disabled={disabled}
            k="all"
            openTip={openTip}
            options={editor.options}
            error={limitErr && err.qwpId && !ownIds.has(err.qwpId)
              ? `${errName(err.qwpId)}: ${limitErrorText(err)}` : ''}
          />
        )}
        {firstNotes.length > 0 && (
          <div className="mt-[10px]">
            {firstNotes.map((n) => (
              <div key={n.key} className={n.cls} data-testid={n.testId}>{n.text}</div>
            ))}
          </div>
        )}
      </div>

      <div className={EXH}>
        <span className={EXH_T}>Packages with a rule of their own</span>
        <span className={EXH_S} data-testid="qh-own-count">{l.rows.length}</span>
      </div>

      {l.rows.map(({
        row, kind, why, value,
      }) => {
        const id = row.qwp_id;
        // A refusal of this package's limit opens its row, to show it at the field.
        const lineLimitErr = limitErr && err.qwpId === id;
        const open = editing.has(id) || lineLimitErr;
        const tba = row.executing && row.executing.appointed === false;
        const onChange = (v) => upd((st) => setPackageRule(st, id, v));
        return (
          <div key={id} className={FB} data-testid={`qh-line-${id}`}>
            <div className={EXROW}>
              <div className={NM}>
                <b>{row.name}</b>
                <div className={T_SUB2} data-testid={`qh-line-tag-${id}`}>
                  {kind === 'auto'
                    ? <span className={T_RWHY}>{`automatic · ${why}`}</span>
                    : <span className="text-[#5b6b85]">own rule</span>}
                  {tba ? ' · to be appointed' : ''}
                </div>
              </div>
              <div className={EXSUM} data-testid={`qh-summary-${id}`}>
                <ChainSummary value={value} row={row} />
                {((value && value.path) || 'none') !== 'none' && (
                  <span className={EXSUM_ST} data-testid={`qh-limit-sum-${id}`}>
                    {`· ${LIMIT_LABEL}: ${formatReceiverLimit(receiverLimitOf(value))}`}
                  </span>
                )}
              </div>
              <div className={ACT}>
                <button
                  type="button"
                  className={BTN_GHOST_SM}
                  onClick={() => toggle(id)}
                  aria-expanded={open}
                  data-testid={`qh-edit-${id}`}
                >
                  {open ? 'Done' : 'Edit'}
                </button>
                {kind === 'own' && editor.canGovern && (
                  <button
                    type="button"
                    className={`${T_X_BTN} text-[15px] px-[4px] disabled:opacity-60`}
                    title="Remove — back to the first block"
                    disabled={disabled}
                    onClick={() => upd((st) => removeOwnRule(section, st, id, ctx))}
                    data-testid={`qr-remove-${section}-${id}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            {review[id] && (
              <div className={T_RWARN} data-testid={`qr-review-${section}-${id}`}>
                {reviewText(review[id])}
              </div>
            )}
            {err && err.qwpId === id && !lineLimitErr && (
              <div className={T_ERR} data-testid={`qr-line-error-${section}-${id}`}>
                {err.message}
              </div>
            )}
            {open && (
              <div className={EXBODY}>
                <div className={FBH}>
                  <span className={FBH_S}>Handover path</span>
                  <PathSelect
                    value={value}
                    row={row}
                    onChange={onChange}
                    disabled={disabled}
                    k={id}
                    editor={editor}
                  />
                  <span className={FBH_S} data-testid={`qh-count-${id}`}>
                    {formatHandoverCount((value && value.path) || 'none')}
                  </span>
                </div>
                <ChainForm
                  value={value}
                  row={row}
                  onChange={onChange}
                  disabled={disabled}
                  k={id}
                  openTip={openTip}
                  options={editor.options}
                />
                {((value && value.path) || 'none') !== 'none' && (
                  <ReceiverLimit
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    k={id}
                    openTip={openTip}
                    options={editor.options}
                    error={lineLimitErr ? limitErrorText(err) : ''}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      <RuleFoot section={section} editor={editor} conf={{ foot: 'select' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// the cards beside each subarea (mockup `SIDE["3a"]`, `SIDE["3b"]`), with the
// D16 texts of the owner where they speak about the same point
// ---------------------------------------------------------------------------
// Mockup `.hcard ol` (v79:358) and `.hcard .fn` (359).
const OL = 'list-decimal ml-[18px] mt-[2px] [&>li]:mb-[6px] [&>li:last-child]:mb-0';
const FN = 'text-[#5b6b85] text-[12px] mt-[10px] pt-[10px] border-t border-[#16223a]';

const SIDE3 = {
  handover: [
    {
      title: 'How a handover works',
      body: (
        <ol className={OL}>
          <li>
            An asset is ready for handover when every line of its checklists is resolved and it
            has no open NCR or RFI.
          </li>
          <li>
            The handing-over company books the handover inspection, giving at least the minimum
            notice set here, and invites the receiving company. A booking with less notice is
            refused; ConstructQA shows the first date allowed.
          </li>
          <li>
            At the inspection, the receiving company reviews the work and raises punch list
            items. Everyone who attended signs their attendance in their own login. The inspection
            is completed once the handing-over company has asked to complete it, everyone present
            has signed or been recorded as &quot;did not sign&quot;, and at least one person from
            the receiving company has signed.
          </li>
          <li>
            The handover of an asset is complete when the inspection is completed and no Category
            A item is waiting to be accepted. There is no separate acceptance signature.
          </li>
        </ol>
      ),
    },
  ],
  punch_list: [
    {
      title: 'About the punch list',
      icon: 'i',
      info: true,
      body: (
        <>
          <p>
            Punch list items are defects or unfinished work found during the handover inspection,
            raised against the asset. Each item gets a category:
          </p>
          <Resp tone="bad">
            Category A — must be fixed and accepted before the handover of the asset can
            complete. Always blocks handover and QA Complete.
          </Resp>
          <Resp tone="warn">
            Category B — must be fixed. Does not hold the handover. May stay open at QA Complete
            only if this rule allows it.
          </Resp>
          <Resp tone="ok">
            Category C — minor or cosmetic. Does not hold the handover. May stay open at QA
            Complete only if this rule allows it.
          </Resp>
          <p className={FN}>
            The rule here says, per package, whether B and C items may still be open at QA
            Complete.
          </p>
        </>
      ),
    },
    {
      title: 'Who closes an item',
      body: (
        <p>
          The responsible company records the rectification; the receiving company accepts or
          rejects it. An item — A, B or C — is closed only when the receiving company accepts the
          rectification.
        </p>
      ),
    },
  ],
};

// ---------------------------------------------------------------------------
// 3b — punch list & QA Complete (mockup `rt("3b", ...)`, `.rt2` look)
// ---------------------------------------------------------------------------

// Mockup `.setbelow` (257), `.chip.dimc` / `.chip.tba` (385), `.rt2 .pk` (440),
// `.splitlnk` (254).
const SETBELOW = 'text-[#94a3b8] text-[12px] italic';
const CHIP = 'inline-flex items-center gap-[6px] text-[10px] font-semibold px-[8px] py-[3px] rounded-full whitespace-nowrap';
const CHIP_DIM = `${CHIP} text-[#5b6b85] border border-[#16223a] bg-transparent`;
const CHIP_TBA = `${CHIP} text-[#fbbf24] border border-[rgba(245,158,11,.4)] bg-[rgba(245,158,11,.08)]`;
const CHIPS = 'flex gap-[6px] flex-wrap items-center';
const PK = 'inline-flex items-center gap-[6px] text-[12px] text-[#f4f7fc] cursor-pointer';
const PK_INPUT = 'accent-[#f97316] w-[14px] h-[14px]';
const SPLITLNK = 'text-[12px] text-[#93c5fd] underline decoration-dotted hover:text-[#f97316] disabled:opacity-60 disabled:cursor-default';

// Mockup `posShort` (v79:1149).
const POSITION_SHORT = {
  execution: 'Executing', verification: 'Verification', acceptance: 'Acceptance',
};

/** Mockup `plTwo`: the rule can be set per handover only where the package HAS
 *  two — its live handover path, else its chain (the server's own branch). */
const twoHandovers = (row, ctx) => punchHasTwoHandovers(
  row, row && ctx && ctx.handoverPathOf ? ctx.handoverPathOf(row.qwp_id) : null,
);
const isSplit = (value, row, ctx) => Boolean(value && value.per_handover)
  && twoHandovers(row, ctx);

const stepAt = (value, i) => ((value && value.handovers) || [])[i] || {};

/** Mockup `gsel(PL, ...)`: the served "No" / "Yes". */
function OpenItemsSelect({
  value, index, onChange, disabled, testId, options,
}) {
  return (
    <ValueSelect
      served={fieldOptions(options, 'punch_list', 'handovers[].qa_complete_with_open_items')}
      current={Boolean(stepAt(value, index).qa_complete_with_open_items)}
      fallback={String}
      disabled={disabled}
      onPick={(v) => onChange(setPunchStep(value, index, { qa_complete_with_open_items: v }))}
      testId={testId}
    />
  );
}

/** Mockup `catsQ`: the B / C chips while items may stay open, and the reminder
 *  that at least one must be chosen; otherwise the closed chip. */
function Categories({
  value, index, onChange, disabled, k, options,
}) {
  const step = stepAt(value, index);
  const cats = step.open_categories || [];
  if (!step.qa_complete_with_open_items) {
    return <span className={CHIP_DIM} data-testid={`qp-cats-${k}-${index}`}>Every item closed</span>;
  }
  // The served categories (D14.5: B and C), in the server's order.
  const served = fieldOptions(options, 'punch_list', 'handovers[].open_categories');
  const toggle = (c, on) => onChange(setPunchStep(value, index, {
    open_categories: on
      ? served.map((o) => o.value).filter((x) => cats.includes(x) || x === c)
      : cats.filter((x) => x !== c),
  }));
  return (
    <div className={CHIPS} data-testid={`qp-cats-${k}-${index}`}>
      {served.map(({ value: c, label }) => (
        <label className={PK} key={c}>
          <input
            type="checkbox"
            className={PK_INPUT}
            checked={cats.includes(c)}
            disabled={disabled}
            onChange={(e) => toggle(c, e.target.checked)}
            data-testid={`qp-cat-${k}-${index}-${c}`}
          />
          {label}
        </label>
      ))}
      {cats.length === 0 && <span className={CHIP_TBA}>Select at least one</span>}
    </div>
  );
}

function PunchOpenCell({
  value, row, ctx, onChange, disabled, k, options,
}) {
  if (isSplit(value, row, ctx)) {
    return <span className={SETBELOW} data-testid={`qp-setbelow-${k}`}>Set per handover below</span>;
  }
  return (
    <OpenItemsSelect
      value={value}
      index={0}
      onChange={onChange}
      disabled={disabled}
      testId={`qp-open-${k}`}
      options={options}
    />
  );
}

function PunchCatsCell({
  value, row, ctx, onChange, disabled, k, options,
}) {
  if (isSplit(value, row, ctx)) return null;
  return (
    <Categories value={value} index={0} onChange={onChange} disabled={disabled} k={k} options={options} />
  );
}

/** Mockup `plLnkCell`: D14.5's own words, "Varies between companies", for a
 *  rule the contract keeps per HANDOVER. */
function PunchLinkCell({
  value, row, ctx, onChange, disabled, k,
}) {
  if (!twoHandovers(row, ctx)) return null;
  const on = Boolean(value && value.per_handover);
  return (
    <div className="text-[12px]">
      <button
        type="button"
        disabled={disabled}
        className={SPLITLNK}
        onClick={() => onChange(setPerHandover(value, !on))}
        data-testid={`qp-split-${k}`}
      >
        {on ? 'Same for all companies' : 'Varies between companies'}
      </button>
    </div>
  );
}

/** Mockup `hoLbl`: the first row names the positions, a package its companies. */
const hopLabel = (row, from, to) => {
  const nm = (p) => (row ? (companyAt(row, p) || '—') : POSITION_SHORT[p]);
  return `${nm(from)} → ${nm(to)}`;
};

/** Mockup `plSub`: one "↳" line per handover when the rule varies. */
function punchSubRows({
  value, row, ctx, k, onChange, disabled, options,
}) {
  if (!isSplit(value, row, ctx)) return [];
  return [
    { key: 'h1', label: hopLabel(row, 'execution', 'verification'), index: 0 },
    { key: 'h2', label: hopLabel(row, 'verification', 'acceptance'), index: 1 },
  ].map(({ key, label, index }) => ({
    key,
    label,
    cells: [
      <OpenItemsSelect
        value={value}
        index={index}
        onChange={onChange}
        disabled={disabled}
        testId={`qp-open-${k}-${key}`}
        options={options}
      />,
      <Categories value={value} index={index} onChange={onChange} disabled={disabled} k={k} options={options} />,
      null,
    ],
  }));
}

// ---------------------------------------------------------------------------
// the summary under 3a (mockup `#foot3sum`, `render3aFlow`'s `sumrows3`)
// ---------------------------------------------------------------------------

/** Mockup `co`: the company at a position, or that the package has none. */
function SummaryParty({ row, position }) {
  const c = companyAt(row, position);
  if (!c) return <span className={T_MISS}>none in this package</span>;
  return <>{c}</>;
}

/** Mockup `stg`: "<from> → <to>" and, under it, the two settings of that
 *  handover in lower case. */
function SummaryStage({
  row, from, to, step,
}) {
  return (
    <>
      <SummaryParty row={row} position={from} />
      {' → '}
      <SummaryParty row={row} position={to} />
      <div className={T_DIMTXT}>
        {`${formatHandoverUnit(step && step.unit).toLowerCase()} · ${formatHandoverNotice(step && step.notice).toLowerCase()}`}
      </div>
    </>
  );
}

const Dash = () => <>—</>;

/** Mockup `#foot3sum`: the rule each package is on and the companies handing
 *  over and receiving at each stage — the editor's state, live. */
function HandoverSummary({ editor }) {
  const [q, setQ] = React.useState('');
  const section = 'handover';
  const state = editor.states[section];
  if (!state) return null;
  const { structure } = sectionCtx(editor, section);
  const review = editor.status[section].review;
  const f = q.trim().toLowerCase();
  const rows = structure.filter((r) => !f || (r.name || '').toLowerCase().includes(f));
  const chain = (r) => [
    r.executing && r.executing.appointed !== false ? r.executing.name : 'To be appointed',
    r.verification && r.verification.name,
    r.acceptance && r.acceptance.name,
  ].filter(Boolean).join(' → ');
  return (
    <div className="col-span-full mt-[22px]" data-testid="qh-summary">
      <div className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px]">
        <div className="flex items-start gap-[16px] mb-[12px]">
          <div className="flex-1">
            <div className="text-[17px] font-bold flex items-center gap-[10px] mb-[8px] flex-wrap">
              Who hands over to whom, per package
            </div>
            <div className="text-[#94a3b8] text-[13px] mb-[16px] max-w-[760px]">
              Current rule per package, with the companies handing over and receiving at each
              stage.
            </div>
            <HowThisWorks testId="qh-how-summary">
              This table shows the current rule assigned to each package and the companies handing
              over and receiving at each stage.
            </HowThisWorks>
          </div>
          <input
            className="w-[220px] mt-[6px] shrink-0 bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] px-[10px] py-[8px] text-[12.5px] placeholder:text-[#3d4c68]"
            placeholder="Search packages…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="qh-sum-search"
          />
        </div>
        <div className="border border-[#16223a] rounded-[9px] overflow-hidden bg-[#101a2e]">
          <table className="w-full border-collapse table-fixed" data-testid="qh-sum-table">
            <thead>
              <tr>
                <th className={T_TH} style={{ width: '18%' }}>Package</th>
                <th className={T_TH} style={{ width: '18%' }}>Rule</th>
                <th className={T_TH} style={{ width: '32%' }}>First handover</th>
                <th className={T_TH} style={{ width: '32%' }}>Second handover</th>
              </tr>
            </thead>
            <tbody className="[&>tr:first-child>td]:border-t-0">
              {rows.length ? rows.map((r) => {
                const id = r.qwp_id;
                const line = state.rows[id] || { kind: 'follow' };
                const v = line.kind === 'follow' ? state.general : line.value;
                const path = (v && v.path) || 'none';
                const steps = (v && v.handovers) || [];
                let rule = <span className={T_RP}>General rule</span>;
                if (line.kind === 'own') rule = <span className={T_RP}>Own rule</span>;
                if (line.kind === 'auto') {
                  rule = <><span className={T_RP}>Automatic</span>{` — ${line.why}`}</>;
                }
                let firstStage = <Dash />;
                if (path === 'none') {
                  if (!r.verification && !r.acceptance) {
                    firstStage = <><span className={T_RP}>No handover</span>{' — internal QA'}</>;
                  }
                } else {
                  firstStage = (
                    <SummaryStage
                      row={r}
                      from="execution"
                      to={path === 'EA' ? 'acceptance' : 'verification'}
                      step={steps[0]}
                    />
                  );
                }
                return (
                  <tr key={id} data-testid={`qh-sum-row-${id}`}>
                    <td className={`${T_SUM_TD} !text-[#f4f7fc] font-semibold [&_b]:block [&_b]:leading-[1.3] ${review[id] ? T_BAD_BAR : ''}`}>
                      <b>{r.name}</b>
                      <div className={`${T_SUB2} text-[#5b6b85]`}>{chain(r)}</div>
                    </td>
                    <td className={T_SUM_TD} data-testid={`qh-sum-rule-${id}`}>{rule}</td>
                    <td className={T_SUM_TD} data-testid={`qh-sum-first-${id}`}>{firstStage}</td>
                    <td className={T_SUM_TD} data-testid={`qh-sum-second-${id}`}>
                      {path === 'EVA' ? (
                        <SummaryStage
                          row={r}
                          from="verification"
                          to="acceptance"
                          step={steps[1]}
                        />
                      ) : <Dash />}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={4} className={`${T_SUM_TD} !text-[#5b6b85] text-center`}>
                    No package matches.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// the subareas (mockup `#s3a`, `#s3b`)
// ---------------------------------------------------------------------------
export const SECTIONS3 = {
  handover: {
    title: 'Handover',
    help: 'Decide who hands the completed work over to whom, and how much notice the receiving party gets.',
    how: (
      <>
        <p>
          <b>What a handover is.</b> When a company finishes a piece of work, it hands the
          completed work — and its quality records — to the next company in the chain, which
          inspects and accepts it. On a project this can happen more than once: the subcontractor
          hands over to the principal contractor, and the principal contractor later hands over to
          the owner. Each of these is a handover, and each is recorded on its own.
        </p>
        <p><b>Handover path</b></p>
        <ul className={UL}>
          <li>Executing → Verification → Acceptance: two handovers</li>
          <li>Executing → Verification: one handover</li>
          <li>Executing → Acceptance: one handover</li>
          <li>Executing Party only: no handover</li>
        </ul>
        <p>
          <b>Minimum notice to the receiving party</b> — how far in advance the handover inspection
          must be booked.
        </p>
        <p>
          <b>All other packages</b> is the rule for every package that has no rule of its own. Add
          a package below when it needs different arrangements.
        </p>
      </>
    ),
    render: (props) => <HandoverFlow {...props} />,
  },
  punch_list: {
    title: 'Punch list & QA Complete',
    help: 'By default, QA Complete needs every punch list item closed. Add a package with its own rule where it may reach QA Complete with Category B or C items still open.',
    how: (
      <>
        Punch list items are raised during the handover inspection. <b>By default, an asset only
        reaches QA Complete when every punch list item is closed.</b> All packages start with this
        default rule; add an exception where the QMP allows selected categories to remain
        open. Category A items always block QA Complete.
      </>
    ),
    // Mockup `#rt3b`: the `.rt2` look of step 2, its lowercase "own rule" tag
    // and the `<select>` footer (`s2 = k[0] === "2"` is false for 3b).
    ownLabel: 'own rule',
    foot: 'select',
    firstWidth: '30%',
    lastWidth: '4%',
    columns: [
      {
        key: 'open',
        label: 'Can an asset reach QA Complete with open punch list items?',
        width: '26%',
        tip: 'pl',
        wrap: true,
      },
      { key: 'cats', label: 'Categories that may stay open', width: '31%', tip: 'plc' },
      { key: 'lnk', label: '', width: '18%' },
    ],
    cells: [PunchOpenCell, PunchCatsCell, PunchLinkCell],
    subRows: punchSubRows,
  },
};

export default function HandoverStep({ editor, sub, onSub }) {
  const { open, pop } = useTip(TIPS3);
  // Mockup `#w3.on` (v79:434): the card, the side cards, and the summary
  // spanning both below them.
  return (
    <div
      className="grid grid-cols-1 min-[1400px]:grid-cols-[minmax(0,1fr)_280px] gap-x-[22px] items-start"
      data-testid="qs-pane-3"
    >
      <div className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px] min-w-0">
        <Subnav sub={sub} onSub={onSub} status={editor.status} subs={SUBS3} titles={editor.titles} />
        <LoadError editor={editor} />
        <SubCard key={sub} section={sub} conf={SECTIONS3[sub]} editor={editor} openTip={open} />
      </div>
      <SideCards section={sub} cards={SIDE3[sub]} />
      {/* Mockup (v79:1484): the summary belongs to 3a only. */}
      {sub === 'handover' && <HandoverSummary editor={editor} />}
      {pop}
    </div>
  );
}
